import { log, logError } from '../../utils/logging';

/**
 * Wraps paragraph OOXML into the minimal package format required by Word's insertOoxml method.
 * 
 * The insertOoxml method typically expects the same format as getOoxml returns - a full package
 * with namespaces and structure. This utility wraps a paragraph OOXML fragment into that format.
 * 
 * Based on Microsoft Office Open XML format:
 * https://learn.microsoft.com/en-us/office/open-xml/word-processing
 */
export class OoxmlPackageWrapper {
  /**
   * Wraps a paragraph OOXML fragment into a minimal package format for insertOoxml
   * @param paragraphOoxml - The paragraph OOXML (w:p element and contents)
   * @returns Package OOXML suitable for insertOoxml, or empty string if wrapping fails
   */
  static wrapParagraphInPackage(paragraphOoxml: string): string {
    if (!paragraphOoxml || paragraphOoxml.trim().length === 0) {
      logError('[OoxmlPackageWrapper] Empty paragraph OOXML provided');
      return '';
    }

    try {
      // Defensive normalization: this wrapper embeds its input inside <w:document><w:body>...
      // If the caller accidentally passes a <w:body>...</w:body> or <w:document>...</w:document>
      // wrapper, the result has nested <w:body> elements which is invalid OOXML and is rejected
      // by Word Win32 `insertOoxml` with GeneralException. Strip outer wrappers so we always
      // embed paragraph-level content.
      const normalizedParagraphOoxml =
        OoxmlPackageWrapper.unwrapToParagraphContent(paragraphOoxml);

      log('[OoxmlPackageWrapper] Wrapping paragraph in package format');
      log(`[OoxmlPackageWrapper] Paragraph OOXML length: ${normalizedParagraphOoxml.length}`);
      log(
        `[OoxmlPackageWrapper] Paragraph OOXML preview: ${normalizedParagraphOoxml.substring(0, 200)}`,
      );

      // Create the minimal OOXML package structure
      // This follows the format returned by getOoxml() - a pkg:package with parts
      const packageXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
  <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
    <pkg:xmlData>
      <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture" xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml">
        <w:body>
          ${normalizedParagraphOoxml}
        </w:body>
      </w:document>
    </pkg:xmlData>
  </pkg:part>
</pkg:package>`;

      log('[OoxmlPackageWrapper] Package created successfully');
      log(`[OoxmlPackageWrapper] Package length: ${packageXml.length}`);
      
      return packageXml;
    } catch (error) {
      logError('[OoxmlPackageWrapper] Error wrapping paragraph in package', error);
      return '';
    }
  }

  /**
   * Strip outer <w:document>/<w:body> wrappers if present, returning paragraph-level content
   * (one or more <w:p> siblings, with any <w:sectPr> stripped to avoid altering section props).
   *
   * Why: callers occasionally pass the result of `paragraph.getOoxml()` extraction that on
   * Win32 includes a <w:body> wrapper with surrounding section context. Embedding such input
   * directly inside <w:body> produces invalid nested <w:body><w:body> OOXML that Win32
   * `insertOoxml` rejects with GeneralException.
   */
  private static unwrapToParagraphContent(input: string): string {
    const trimmed = input.trim();
    if (!trimmed) return trimmed;

    // Fast path: already a single <w:p> (or its self-closing variant).
    if (/^<w:p[\s>/]/i.test(trimmed)) return trimmed;

    try {
      const parser = new DOMParser();
      // Use a permissive root to avoid namespace prefix issues when parsing fragments.
      const wrapped = `<root xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml" xmlns:w16du="http://schemas.microsoft.com/office/word/2023/wordml/word16du" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">${trimmed}</root>`;
      const doc = parser.parseFromString(wrapped, 'text/xml');
      const parseError = doc.getElementsByTagName('parsererror')[0];
      if (parseError) return trimmed;

      const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
      const pNodes = doc.getElementsByTagNameNS(W_NS, 'p');
      if (pNodes.length === 0) return trimmed;

      const serializer = new XMLSerializer();
      let out = '';
      for (let i = 0; i < pNodes.length; i++) {
        out += serializer.serializeToString(pNodes.item(i)!);
      }
      return out || trimmed;
    } catch {
      return trimmed;
    }
  }

  /**
   * Return a getOoxml() package with the body trimmed to contain exactly the target paragraph.
   *
   * Use this when you need to round-trip Word's own paragraph.getOoxml() result back into
   * Range.insertOoxml(...) to faithfully restore that paragraph (all original styles,
   * tracked changes, formatting, run properties, etc.) without manually reconstructing OOXML.
   *
   * Why this is needed:
   *   - On Word Mac, `paragraph.getOoxml()` typically returns a package whose body contains a
   *     single <w:p>. Inserting that package back via Range.insertOoxml works directly.
   *   - On Word Win32, `paragraph.getOoxml()` can return a package whose body contains the
   *     target paragraph PLUS trailing context (siblings, <w:sectPr>). Inserting that package
   *     unchanged would duplicate or shift the surrounding paragraphs.
   *   - A hand-crafted minimal package (see `wrapParagraphInPackage`) is rejected by Word Win32
   *     `insertOoxml` with `GeneralException` for many real-world paragraphs because the
   *     hand-crafted wrapper lacks namespace declarations / package parts that Win32 requires.
   *
   * Strategy:
   *   - Parse the package, find <w:body>, keep ONLY the target <w:p>, drop all sibling <w:p>
   *     and <w:sectPr> elements. Preserve every other aspect of Word's original output
   *     (namespaces, package parts, content types, attribute order).
   *   - This is round-trip-safe: Word reliably re-ingests its own output.
   *
   * @param packageXml The pkg:package XML returned by `paragraph.getOoxml()`
   * @param targetParagraphText Plain text of the target paragraph (used to disambiguate when
   *   the body contains multiple <w:p>). Optional but strongly recommended on Win32.
   * @returns A pkg:package XML containing only the target paragraph in <w:body>, or empty
   *   string if parsing failed.
   */
  static keepOnlyTargetParagraphInPackage(
    packageXml: string,
    targetParagraphText?: string | null,
  ): string {
    if (!packageXml?.trim()) {
      logError('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: empty package');
      return '';
    }

    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const normalize = (s: string): string => s.replace(/\s+/g, ' ').trim();
    const target =
      targetParagraphText != null && targetParagraphText.length > 0
        ? normalize(targetParagraphText)
        : null;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(packageXml, 'text/xml');
      const parserError = doc.getElementsByTagName('parsererror')[0];
      if (parserError) {
        logError(
          '[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: parse error',
          parserError.textContent,
        );
        return '';
      }

      const bodyNodes = doc.getElementsByTagNameNS(W_NS, 'body');
      if (bodyNodes.length === 0) {
        // Some packages may not declare the namespace on the root; try local-name scan.
        const fallback = Array.from(doc.getElementsByTagName('*')).find(
          (el) => (el.localName || el.tagName.split(':').pop()) === 'body',
        );
        if (!fallback) {
          log('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: no <w:body> found');
          return '';
        }
        return OoxmlPackageWrapper.pruneBodyToTargetParagraph(doc, fallback, target);
      }
      return OoxmlPackageWrapper.pruneBodyToTargetParagraph(doc, bodyNodes.item(0)!, target);
    } catch (error) {
      logError('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage error', error);
      return '';
    }
  }

  private static pruneBodyToTargetParagraph(
    doc: Document,
    body: Element,
    targetNormalizedText: string | null,
  ): string {
    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const normalize = (s: string): string => s.replace(/\s+/g, ' ').trim();

    const childParagraphs: Element[] = [];
    const childSectPrs: Element[] = [];
    const otherChildren: Element[] = [];

    for (let i = 0; i < body.childNodes.length; i++) {
      const node = body.childNodes.item(i);
      if (node.nodeType !== 1) continue;
      const el = node as Element;
      const localName = el.localName || el.tagName.split(':').pop();
      if (el.namespaceURI === W_NS || !el.namespaceURI) {
        if (localName === 'p') childParagraphs.push(el);
        else if (localName === 'sectPr') childSectPrs.push(el);
        else otherChildren.push(el);
      } else {
        otherChildren.push(el);
      }
    }

    if (childParagraphs.length === 0) {
      log('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: no <w:p> in body');
      return '';
    }

    let chosen: Element | null = null;
    if (targetNormalizedText && targetNormalizedText.length > 0) {
      for (const p of childParagraphs) {
        if (normalize(p.textContent || '') === targetNormalizedText) {
          chosen = p;
          break;
        }
      }
    }
    if (!chosen) {
      for (const p of childParagraphs) {
        if (normalize(p.textContent || '').length > 0) {
          chosen = p;
          break;
        }
      }
    }
    if (!chosen) chosen = childParagraphs[0];

    for (const p of childParagraphs) {
      if (p !== chosen) body.removeChild(p);
    }
    for (const sectPr of childSectPrs) {
      body.removeChild(sectPr);
    }

    const serializer = new XMLSerializer();
    const out = serializer.serializeToString(doc);
    log('[OoxmlPackageWrapper] keepOnlyTargetParagraphInPackage: pruned body', {
      originalParagraphCount: childParagraphs.length,
      droppedSectPrCount: childSectPrs.length,
      droppedOtherChildren: otherChildren.length,
      outputLen: out.length,
    });
    return out;
  }

  /**
   * Replace the single w:p inside a getOoxml() package with accepted paragraph XML.
   * Preserves Word's original package shape (namespaces, parts) — insertOoxml expects this format.
   */
  static replaceParagraphInPackage(originalPackageXml: string, newParagraphOoxml: string): string {
    const para = (newParagraphOoxml || '').trim();
    if (!originalPackageXml?.trim() || !para) {
      logError('[OoxmlPackageWrapper] replaceParagraphInPackage: missing input');
      return '';
    }

    const paragraphCount = (originalPackageXml.match(/<w:p\b/gi) ?? []).length;
    if (paragraphCount !== 1) {
      log('[OoxmlPackageWrapper] replaceParagraphInPackage: expected 1 w:p, falling back to wrap', {
        paragraphCount,
      });
      return this.wrapParagraphInPackage(para);
    }

    const replaced = originalPackageXml.replace(/<w:p\b[^>]*>[\s\S]*?<\/w:p>/i, para);
    if (replaced === originalPackageXml) {
      log('[OoxmlPackageWrapper] replaceParagraphInPackage: no w:p matched, falling back to wrap');
      return this.wrapParagraphInPackage(para);
    }

    log('[OoxmlPackageWrapper] replaceParagraphInPackage: replaced w:p in live package');
    return replaced;
  }

  /**
   * Validates that the wrapped package is well-formed XML
   * @param packageXml - The package XML to validate
   * @returns true if valid, false otherwise
   */
  static validatePackage(packageXml: string): boolean {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(packageXml, 'text/xml');
      
      // Check for parsing errors
      const parserError = doc.querySelector('parsererror');
      if (parserError) {
        logError('[OoxmlPackageWrapper] Package validation failed - parse error', parserError.textContent);
        return false;
      }
      
      // Check for required structure
      const packageElement = doc.querySelector('package, pkg\\:package');
      if (!packageElement) {
        logError('[OoxmlPackageWrapper] Package validation failed - no package element found');
        return false;
      }
      
      log('[OoxmlPackageWrapper] Package validation passed');
      return true;
    } catch (error) {
      logError('[OoxmlPackageWrapper] Package validation failed with error', error);
      return false;
    }
  }
}
